import type { WineDetail } from "@corklog/shared";
import { useTranslation } from "react-i18next";

/**
 * What the window used to say.
 *
 * Two facts, one line, and they are not the same fact. **What the import
 * said** is permanent: it lives on the wine, the import wrote it once, and no
 * number of saves touches it — which is the whole point, because 95 of the 123
 * wines here carry a single year that arrived with the spreadsheet and the
 * four working years move every time a suggestion is accepted or the form is
 * saved. **What the last suggestion replaced** is the narrower one: useful when
 * the year it wrote over was the owner's own judgement or an earlier proposal,
 * and redundant when it was the import's, which this file already says.
 *
 * **One line of small text, and never a second band.** The band is the loudest
 * thing in this interface and says one thing: where this wine stands today. A
 * second band drawn from a year that no longer applies would compete with it
 * and mean nothing.
 *
 * Nothing is shown where nothing changed. A suggestion accepted for the peak
 * alone did not replace the `drinkBy` beside it, and a year the model landed on
 * that equals the import's replaced nothing at all.
 */
const YEARS = ["drinkFrom", "peakFrom", "peakTo", "drinkBy"] as const;

const IMPORTED = {
  drinkFrom: "importedDrinkFrom",
  peakFrom: "importedPeakFrom",
  peakTo: "importedPeakTo",
  drinkBy: "importedDrinkBy",
} as const;

export function ReplacedWindow({ wine }: { wine: WineDetail }) {
  const { t } = useTranslation();

  const imported = YEARS.filter((year) => (wine[IMPORTED[year]] ?? null) !== null).map(
    (year) => ({
      year,
      value: wine[IMPORTED[year]] as number,
      stillStands: wine[IMPORTED[year]] === wine[year],
    }),
  );

  // Only where it is not the import's own year saying the same thing again.
  const replaced =
    (wine.replacedWindow ?? null) === null
      ? []
      : YEARS.filter((year) => {
          const was = wine.replacedWindow?.[year] ?? null;
          return (
            was !== null && was !== wine[year] && was !== (wine[IMPORTED[year]] ?? null)
          );
        });

  if (imported.length === 0 && replaced.length === 0) return null;

  const parts: string[] = [];
  if (imported.length > 0) {
    parts.push(
      t("window.history.imported", {
        years: imported
          .map((entry) =>
            t(entry.stillStands ? "window.history.yearStanding" : "window.history.year", {
              field: t(`wines.${entry.year}`).toLocaleLowerCase("sv-SE"),
              year: entry.value,
            }),
          )
          .join(" · "),
      }),
    );
  }
  if (replaced.length > 0) {
    const window = wine.replacedWindow;
    parts.push(
      t("window.history.replaced", {
        years: replaced
          .map((year) =>
            t(`window.history.replacedYear.${window?.[`${year}Source`] ?? "unknown"}`, {
              field: t(`wines.${year}`).toLocaleLowerCase("sv-SE"),
              year: window?.[year],
            }),
          )
          .join(" · "),
      }),
    );
  }

  return <p className="text-muted mt-2 text-xs">{parts.join(" — ")}</p>;
}
